import { useMemo } from 'react';
import { LTSSService, FinancialSummary, Program, ClientUtilization } from '../types/ltss';
import { getProgramLimits, ProgramLimitsConfig } from '../components/Settings/ProgramLimits';

// Derive rate per unit from paid claims data
function deriveRatePerUnit(
  services: LTSSService[],
  program: Program,
  limits: ProgramLimitsConfig
): number {
  // Check if manually set in limits
  const programLimit = limits[program];
  if (programLimit?.ratePerUnit !== null && programLimit?.ratePerUnit !== undefined) {
    return programLimit.ratePerUnit;
  }

  // Derive from paid claims for this program
  let totalPaid = 0;
  let totalUnits = 0;

  services.forEach(service => {
    if (
      service.program === program &&
      service.amountPaid &&
      service.amountPaid > 0 &&
      service.unitsBilled &&
      service.unitsBilled > 0 &&
      (service.claimHistory === 'Original Claim Paid' || service.claimHistory === 'Adjustment Claim Paid')
    ) {
      totalPaid += service.amountPaid;
      totalUnits += service.unitsBilled;
    }
  });

  return totalUnits > 0 ? totalPaid / totalUnits : 6.40; // Default fallback
}

export function useFinancials(services: LTSSService[], clientUtilization?: ClientUtilization[]): FinancialSummary {
  return useMemo(() => {
    const limits = getProgramLimits();
    
    let totalUnitsBilled = 0;
    let totalAmountPaid = 0;
    let claimsPaid = 0;
    let claimsPending = 0;
    let claimsNotSubmitted = 0;
    let claimsVoided = 0;
    
    // Track expected pay by program for accurate leakage calculation
    const programRates: Record<string, number> = {};

    // First pass: derive rates per program from paid claims
    const programs: Program[] = ['CFC', 'CO', 'CPAS', 'EPSDT', 'REM'];
    programs.forEach(program => {
      programRates[program] = deriveRatePerUnit(services, program, limits);
    });

    services.forEach(service => {
      totalUnitsBilled += service.unitsBilled || 0;
      totalAmountPaid += service.amountPaid || 0;

      switch (service.claimHistory) {
        case 'Original Claim Paid':
        case 'Adjustment Claim Paid':
          claimsPaid++;
          break;
        case 'Original Claim Submitted':
        case 'Adjustment In Progress':
          claimsPending++;
          break;
        case 'Original Claim NotSubmitted':
          claimsNotSubmitted++;
          break;
        case 'Void Claim Paid':
          claimsVoided++;
          break;
      }
    });

    // Leakage = expected value of all billable units - what was actually paid
    // This includes submitted but unpaid and not-submitted claims
    const totalExpectedValue = totalUnitsBilled * (Object.values(programRates).reduce((a, b) => a + b, 0) / programs.length || 6.40);
    const estimatedLeakage = Math.max(0, totalExpectedValue - totalAmountPaid);

    // Total missed revenue from underutilized authorized hours
    const totalMissedRevenue = clientUtilization 
      ? clientUtilization.reduce((sum, c) => sum + c.missedRevenue, 0)
      : 0;

    return {
      totalUnitsBilled,
      totalAmountPaid,
      claimsPaid,
      claimsPending,
      claimsNotSubmitted,
      claimsVoided,
      estimatedLeakage,
      totalMissedRevenue,
    };
  }, [services, clientUtilization]);
}
